import { SaudiRiyal } from "../../components/SaudiRiyal";
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Loader2, Eye, CheckCircle2, RotateCcw, Search, Printer, FileDown, Pencil } from "lucide-react";
import { printJournal, pdfJournal } from "../lib/voucherPrint";
import { toast } from "sonner";
import { finApi, money, apiErr } from "../api";
import { useFin, PageHeader, Card } from "../FinanceApp";
import { StatusBadge } from "./Dashboard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const today = () => new Date().toLocaleDateString("en-CA");
const emptyLine = () => ({ account_id: "", debit: "", credit: "", description: "" });

export default function FinJournals() {
  const { can, user } = useFin();
  const [editing, setEditing] = useState(null);
  const canDelPosted = ["super_admin", "cfo"].includes(user?.role);
  const canDel = (e) => (e.status === "draft" ? can("journals.delete") : canDelPosted && ["manual", "reversal", undefined, null].includes(e.source) && !e.reversed_entry_id);
  const remove = async (e) => {
    const msg = e.status === "draft" ? `حذف القيد ${e.number}؟` : `تحذير: القيد ${e.number} مُرحّل وحذفه يغيّر أرصدة الحسابات والتقارير.\nالأصل المحاسبي هو القيد العكسي. هل تريد الحذف نهائياً؟`;
    if (!window.confirm(msg)) return false;
    try { await finApi.deleteJournal(e.id); toast.success("تم حذف القيد"); load(); return true; } catch (x) { toast.error(apiErr(x)); return false; }
  };
  const doc = async (e, fn) => {
    try { const full = await finApi.journal(e.id); if ((await fn(full, user?.company)) === false) toast.error("اسمح بالنوافذ المنبثقة للطباعة"); }
    catch (x) { toast.error(apiErr(x)); }
  };
  const edit = async (e) => { try { setEditing(await finApi.journal(e.id)); setOpenNew(true); } catch (x) { toast.error(apiErr(x)); } };
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const [j, a] = await Promise.all([finApi.journals(params), finApi.accounts(false)]);
      setRows(j); setAccounts(a);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const shownRows = rows.filter((e) => (!from || (e.date || "") >= from) && (!to || (e.date || "") <= to));
  const postable = accounts.filter((a) => a.allow_posting && a.active);

  return (
    <div data-testid="fin-journals">
      <PageHeader title="القيود اليومية" subtitle="نظام قيود بالقيد المزدوج — لا يُحفظ قيد غير متوازن"
        action={can("journals.create") && <Button onClick={() => { setEditing(null); setOpenNew(true); }} data-testid="fin-add-journal" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><Plus className="h-4 w-4" /> قيد جديد</Button>} />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="بحث برقم القيد أو البيان…" className="pr-9" data-testid="fin-journal-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="fin-journal-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            <SelectItem value="draft">مسودة</SelectItem>
            <SelectItem value="posted">مُرحّل</SelectItem>
            <SelectItem value="reversed">معكوس</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="fin-journal-from" title="من تاريخ" />
        <Input type="date" dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="fin-journal-to" title="إلى تاريخ" />
        <Button variant="outline" onClick={load} data-testid="fin-journal-search-btn">بحث</Button>
        {(q || from || to || status !== "all") && <Button variant="ghost" data-testid="fin-journal-clear" onClick={() => { setQ(""); setFrom(""); setTo(""); setStatus("all"); setTimeout(load, 0); }}>مسح</Button>}
      </div>

      <Card className="overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div>
          : shownRows.length === 0 ? <div className="py-16 text-center text-slate-400" data-testid="fin-journals-empty">لا توجد قيود</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right" data-testid="fin-journals-table">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr><th className="px-3 py-2 font-semibold">رقم القيد</th><th className="px-3 py-2 font-semibold">التاريخ</th><th className="px-3 py-2 font-semibold">البيان</th><th className="px-3 py-2 font-semibold">المرجع</th><th className="px-3 py-2 font-semibold">المبلغ</th><th className="px-3 py-2 font-semibold">الحالة</th><th className="px-3 py-2 font-semibold"></th></tr>
                </thead>
                <tbody className="font-mono-nums">
                  {shownRows.map((e) => (
                    <tr key={e.id} data-testid={`fin-journal-row-${e.number}`} className="border-t border-slate-50 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-semibold text-slate-800">{e.number}</td>
                      <td className="px-3 py-2 text-slate-500">{e.date}</td>
                      <td className="px-3 py-2 font-sans">{e.description || "—"}</td>
                      <td className="px-3 py-2 text-slate-400 font-sans">{e.reference || "—"}</td>
                      <td className="px-3 py-2" dir="ltr">{money(e.total_debit)} <SaudiRiyal /></td>
                      <td className="px-3 py-2 font-sans"><StatusBadge s={e.status} /></td>
                      <td className="px-3 py-2"><div className="flex items-center gap-0.5">
                        <Act onClick={() => setDetail(e.id)} tid={`fin-view-journal-${e.number}`} title="عرض"><Eye className="h-4 w-4" /></Act>
                        <Act onClick={() => doc(e, printJournal)} tid={`fin-print-journal-${e.number}`} title="طباعة"><Printer className="h-4 w-4" /></Act>
                        <Act onClick={() => doc(e, pdfJournal)} tid={`fin-pdf-journal-${e.number}`} title="تنزيل PDF"><FileDown className="h-4 w-4" /></Act>
                        {e.status === "draft" && can("journals.edit") && <Act onClick={() => edit(e)} tid={`fin-edit-journal-${e.number}`} title="تعديل"><Pencil className="h-4 w-4" /></Act>}
                        {canDel(e) && <Act onClick={() => remove(e)} tid={`fin-delete-journal-${e.number}`} title="حذف" danger><Trash2 className="h-4 w-4" /></Act>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <NewJournalDialog open={openNew} onOpenChange={setOpenNew} accounts={postable} onSaved={load} can={can} entry={editing} />
      <JournalDetailDialog id={detail} onClose={() => setDetail(null)} onChanged={load} can={can}
        extra={(e) => <>
          <Button variant="outline" onClick={() => doc(e, printJournal)} className="gap-1.5" data-testid="fin-detail-print"><Printer className="h-4 w-4" /> طباعة</Button>
          {e.status === "draft" && can("journals.edit") && <Button variant="outline" onClick={() => { setDetail(null); edit(e); }} className="gap-1.5" data-testid="fin-detail-edit"><Pencil className="h-4 w-4" /> تعديل</Button>}
          {canDel(e) && <Button variant="outline" onClick={async () => { if (await remove(e)) setDetail(null); }} className="gap-1.5 border-rose-200 text-rose-600" data-testid="fin-detail-delete"><Trash2 className="h-4 w-4" /> حذف</Button>}
        </>} />
    </div>
  );
}

const Act = ({ onClick, tid, title, danger, children }) => (
  <button onClick={onClick} data-testid={tid} title={title} className={`p-1.5 rounded-lg text-slate-400 transition-colors hover:bg-slate-100 ${danger ? "hover:text-rose-600" : "hover:text-slate-700"}`}>{children}</button>
);

function NewJournalDialog({ open, onOpenChange, accounts, onSaved, can, entry }) {
  const [head, setHead] = useState({ date: today(), reference: "", description: "" });
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (entry) {
      setHead({ date: entry.date, reference: entry.reference || "", description: entry.description || "" });
      const ls = (entry.lines || []).map((l) => ({ account_id: l.account_id, debit: l.debit ? String(l.debit) : "", credit: l.credit ? String(l.credit) : "", description: l.description || "" }));
      setLines(ls.length >= 2 ? ls : [...ls, emptyLine(), emptyLine()].slice(0, 2));
    } else { setHead({ date: today(), reference: "", description: "" }); setLines([emptyLine(), emptyLine()]); }
  }, [open, entry]);

  const totals = useMemo(() => {
    const d = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
    const c = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
    return { d: Math.round(d * 100) / 100, c: Math.round(c * 100) / 100 };
  }, [lines]);
  const balanced = totals.d === totals.c && totals.d > 0;

  const setLine = (i, patch) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const save = async (post) => {
    setBusy(true);
    try {
      const body = {
        ...head, post,
        lines: lines.filter((l) => l.account_id && (parseFloat(l.debit) || parseFloat(l.credit)))
          .map((l) => ({ account_id: l.account_id, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0, description: l.description })),
      };
      if (entry) await finApi.updateJournal(entry.id, body); else await finApi.createJournal(body);
      toast.success(post ? "تم حفظ القيد وترحيله" : entry ? "تم تعديل القيد" : "تم حفظ القيد كمسودة");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader className="text-start"><DialogTitle>{entry ? `تعديل القيد ${entry.number}` : "قيد يومية جديد"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>التاريخ</Label><Input type="date" value={head.date} onChange={(e) => setHead({ ...head, date: e.target.value })} data-testid="fin-journal-date" /></div>
            <div className="space-y-1.5"><Label>المرجع</Label><Input value={head.reference} onChange={(e) => setHead({ ...head, reference: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>البيان</Label><Input value={head.description} onChange={(e) => setHead({ ...head, description: e.target.value })} data-testid="fin-journal-desc" /></div>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm text-right">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-2 py-2 font-semibold">الحساب</th><th className="px-2 py-2 font-semibold w-28">مدين</th><th className="px-2 py-2 font-semibold w-28">دائن</th><th className="w-8"></th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid={`fin-line-${i}`}>
                    <td className="px-2 py-1.5">
                      <Select value={l.account_id} onValueChange={(v) => setLine(i, { account_id: v })}>
                        <SelectTrigger className="h-9" data-testid={`fin-line-account-${i}`}><SelectValue placeholder="اختر حساباً" /></SelectTrigger>
                        <SelectContent className="max-h-64">{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-2 py-1.5"><Input dir="ltr" type="number" value={l.debit} onChange={(e) => setLine(i, { debit: e.target.value, credit: "" })} className="h-9" data-testid={`fin-line-debit-${i}`} /></td>
                    <td className="px-2 py-1.5"><Input dir="ltr" type="number" value={l.credit} onChange={(e) => setLine(i, { credit: e.target.value, debit: "" })} className="h-9" data-testid={`fin-line-credit-${i}`} /></td>
                    <td className="px-1">{lines.length > 2 && <button onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 font-mono-nums text-sm">
                <tr className="border-t border-slate-200"><td className="px-2 py-2 font-sans font-semibold text-slate-600">الإجمالي</td><td className="px-2 py-2" dir="ltr">{money(totals.d)} <SaudiRiyal /></td><td className="px-2 py-2" dir="ltr">{money(totals.c)} <SaudiRiyal /></td><td></td></tr>
              </tfoot>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])} className="gap-1.5" data-testid="fin-add-line"><Plus className="h-4 w-4" /> إضافة سطر</Button>
            <span data-testid="fin-balance-indicator" className={`text-sm font-semibold ${balanced ? "text-emerald-600" : "text-rose-500"}`}>
              {balanced ? "✓ القيد متوازن" : `الفرق: ${money(Math.abs(totals.d - totals.c))}`}
            </span>
          </div>
        </div>
        <DialogFooter className="flex-row-reverse flex-wrap gap-2 sm:justify-start">
          {can("journals.post") && <Button onClick={() => save(true)} disabled={busy || !balanced} data-testid="fin-save-post-journal" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ وترحيل</Button>}
          <Button variant="outline" onClick={() => save(false)} disabled={busy || !balanced} data-testid="fin-save-draft-journal">حفظ كمسودة</Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JournalDetailDialog({ id, onClose, onChanged, can, extra }) {
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (id) { setE(null); finApi.journal(id).then(setE).catch((x) => toast.error(apiErr(x))); } }, [id]);

  const post = async () => { setBusy(true); try { await finApi.postJournal(id); toast.success("تم الترحيل"); onChanged(); onClose(); } catch (x) { toast.error(apiErr(x)); } finally { setBusy(false); } };
  const reverse = async () => {
    const reason = window.prompt("سبب العكس:") ?? "";
    setBusy(true); try { await finApi.reverseJournal(id, reason); toast.success("تم إنشاء القيد العكسي"); onChanged(); onClose(); } catch (x) { toast.error(apiErr(x)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={Boolean(id)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="font-body sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader className="text-start"><DialogTitle>{e ? `القيد ${e.number}` : "تحميل…"}</DialogTitle></DialogHeader>
        {!e ? <div className="py-10 text-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500 mx-auto" /></div> : (
          <div className="space-y-3" data-testid="fin-journal-detail">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Info label="التاريخ" v={e.date} /><Info label="الحالة" v={<StatusBadge s={e.status} />} />
              <Info label="المرجع" v={e.reference || "—"} /><Info label="البيان" v={e.description || "—"} />
            </div>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-right"><thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 font-semibold">الحساب</th><th className="px-3 py-2 font-semibold">مدين</th><th className="px-3 py-2 font-semibold">دائن</th></tr></thead>
                <tbody className="font-mono-nums">{e.lines.map((l, i) => (
                  <tr key={i} className="border-t border-slate-50"><td className="px-3 py-2 font-sans">{l.account_code} · {l.account_name}</td><td className="px-3 py-2" dir="ltr">{money(l.debit)} <SaudiRiyal /></td><td className="px-3 py-2" dir="ltr">{money(l.credit)} <SaudiRiyal /></td></tr>
                ))}</tbody>
                <tfoot className="bg-slate-50 font-mono-nums"><tr className="border-t border-slate-200"><td className="px-3 py-2 font-sans font-semibold">الإجمالي</td><td className="px-3 py-2" dir="ltr">{money(e.total_debit)} <SaudiRiyal /></td><td className="px-3 py-2" dir="ltr">{money(e.total_credit)} <SaudiRiyal /></td></tr></tfoot>
              </table>
            </div>
          </div>
        )}
        <DialogFooter className="flex-row-reverse flex-wrap gap-2 sm:justify-start">
          {e?.status === "draft" && can("journals.post") && <Button onClick={post} disabled={busy} data-testid="fin-post-journal-btn" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"><CheckCircle2 className="h-4 w-4" /> ترحيل</Button>}
          {e?.status === "posted" && can("journals.post") && <Button onClick={reverse} disabled={busy} variant="outline" data-testid="fin-reverse-journal-btn" className="gap-1.5 border-amber-300 text-amber-700"><RotateCcw className="h-4 w-4" /> قيد عكسي</Button>}
          {e && extra?.(e)}
          <Button variant="ghost" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, v }) => (<div className="rounded-lg bg-slate-50 px-3 py-2"><span className="text-xs text-slate-400">{label}: </span><span className="font-semibold text-slate-700">{v}</span></div>);
